import React from 'react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import ExportButton from '../ExportButton';
import { BULK_BILL_CATEGORIES } from './sponsorshipConstants';

export const SpendingSummaryTab = ({ children, expenses }) => {
  const totals = {};
  expenses.forEach((e) => {
    totals[e.child_id] = (totals[e.child_id] || 0) + parseFloat(e.amount || 0);
  });
  const rows = children
    .map((c) => ({
      ...c,
      total_spent: totals[c.id] || 0,
      expense_count: expenses.filter((e) => e.child_id === c.id).length,
    }))
    .sort((a, b) => b.total_spent - a.total_spent);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading">Per-Child Spending Summary</CardTitle>
        <CardDescription>Total spent on each child across all expense categories</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">No children registered yet</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Child</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>School</TableHead>
                  <TableHead className="text-right">Expenses</TableHead>
                  <TableHead className="text-right">Account Balance</TableHead>
                  <TableHead className="text-right">Total Spent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c, i) => (
                  <TableRow key={c.id} data-testid={`spending-row-${c.id}`}>
                    <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.grade || '—'}</TableCell>
                    <TableCell>{c.school || '—'}</TableCell>
                    <TableCell className="text-right">{c.expense_count}</TableCell>
                    <TableCell className="text-right">{formatCurrency(c.account_balance || 0)}</TableCell>
                    <TableCell className="text-right font-bold text-red-600">
                      {formatCurrency(c.total_spent)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BulkBillTab = ({ expenses }) => (
  <Card>
    <CardHeader>
      <CardTitle className="font-heading">Bulk Bill Generator</CardTitle>
      <CardDescription>
        Generate a consolidated bill for any expense category showing how much each child contributes to the total.
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {BULK_BILL_CATEGORIES.map((cat) => {
          const catExpenses = expenses.filter((e) => e.expense_type === cat.value);
          const subtotal = catExpenses.reduce((s, e) => s + parseFloat(e.amount || 0), 0);
          const childCount = new Set(catExpenses.map((e) => e.child_id)).size;
          return (
            <Card key={cat.value} className="card-hover" data-testid={`bulk-bill-card-${cat.value}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{cat.label}</CardTitle>
                <CardDescription>{childCount} child{childCount !== 1 ? 'ren' : ''} · {catExpenses.length} entries</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-primary">{formatCurrency(subtotal)}</p>
                <div className="flex gap-2 mt-3">
                  <ExportButton
                    path="exports/sponsorship-bulk-bill"
                    filenameBase={`bulk_bill_${cat.value}`}
                    label="Bill"
                    extraParams={{ category: cat.value }}
                    testId={`bulk-bill-${cat.value}`}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </CardContent>
  </Card>
);
